import type { QueryClient } from '@tanstack/react-query'
import { createContext, useContext, type ReactNode } from 'react'
import type { HttpClient } from '@/shared/api'
import type { AuthProvider } from '@/shared/auth/auth-provider'
import type { RuntimeConfig } from '@/shared/config/schema'
import type { AppModule } from '@/shared/modules/types'
import type { RealtimeClient } from '@/shared/realtime/types'

/**
 * Application services, created once in `src/app/bootstrap` and injected (React context + router
 * context). No module-level singletons.
 */
export interface AppServices {
  config: RuntimeConfig
  http: HttpClient
  auth: AuthProvider
  realtime: RealtimeClient
  queryClient: QueryClient
  /** Modules enabled by runtime configuration (already filtered). */
  modules: readonly AppModule[]
}

/** TanStack Router context available in `beforeLoad` / `loader`. */
export interface RouterContext {
  services: AppServices
}

const ServicesContext = createContext<AppServices | null>(null)

export function ServicesProvider({
  services,
  children,
}: {
  services: AppServices
  children: ReactNode
}) {
  return <ServicesContext.Provider value={services}>{children}</ServicesContext.Provider>
}

export function useServices(): AppServices {
  const services = useContext(ServicesContext)
  if (!services) throw new Error('useServices must be used inside <ServicesProvider>')
  return services
}
